<template>
  <div class="component-weather-widget">
    <div :class="{
      'weather-widget-header': true,
      'weather-widget-header--three-params': data.mode,
      }">
        <p class="weather-widget-header__avg-temp" v-html="'średnia temperatura /&nbsp;'" />
        <p class="weather-widget-header__month" v-html="month" />  
      </div>
    <v-touch @swipeleft="previousMode" @swiperight="nextMode"
      :class="{
        'weather-widget': true,
        'weather-widget--three-params': data.mode && !isMobile,
        }">
      <div v-if="!data.mode || isMobile" class="weather-widget-body">
        <span class="weather-widget__arrow weather-widget__previous" @click.prevent="previousMode">
          <Icon icon="v" />
        </span>

        <div class="weather-widget__display">
          <div :class="{
              'weather-widget__pictogram': true,
              [`weather-widget__pictogram--${pictogram(mode)}`]: true,
            }" />      
          <div class="weather-widget-temperature-wrapper weather-widget-temperature-wrapper--active">
            <div class="weather-widget__info" v-html="selectedMode.info" />
            <div class="weather-widget__temperature" v-html="`${temperature(mode)}&deg;C`" />
          </div>
        </div>

        <span class="weather-widget__arrow weather-widget__next" @click.prevent="nextMode">
          <Icon icon="v" />
        </span>
      </div>

      <div v-else class="weather-widget-body-three-params-wrapper">
        <div class="weather-widget-body" v-for="(mode, key, index) in modes" :key="index">
          <div class="weather-widget__display">
            <div :class="{
                'weather-widget__pictogram': true,
                [`weather-widget__pictogram--${pictogram(key)}`]: true,
              }" />      
            <div class="weather-widget-temperature-wrapper">
              <div class="weather-widget__info" v-html="modes[key].info" />
              <div class="weather-widget__temperature" v-html="`${temperature(key)}&deg;C`" />
            </div>
          </div>
        </div>
      </div>
        
      <span class="weather-widget__more" @click="open" v-html="'zobacz cały sezon'"/>
    </v-touch>

    <WeatherModal v-if="modal" @close="close" :data="weatherData" />

  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'
import VueTouch from 'vue-touch'

import Icon from '@/components/Icon'
import WeatherModal from '@/components/weather-widget/WeatherModal'
import WeatherWidgetMixin from '@/components/WeatherWidgetMixin'
import MediaDetector from '@/utils/MediaDetector'

Vue.use(VueTouch)

@Component({
  mixins: [
    MediaDetector,
    WeatherWidgetMixin,
  ],
  components: {
    WeatherModal,
    Icon,
  },
  props: {
    data: {
      type: Object,
      required: false,
    },
  },
})
export default class WeatherWidget extends Vue {
  weatherData = cloneDeep(this.data)
  modes = {
    'day': { info: 'dzień', temp: 0.0, pictogram: '' },
    'night': { info: 'noc', temp: 0.0, pictogram: '' },
    'water': { info: 'woda', temp: 0.0, pictogram: '' },
  }
  mode = 'day'
  modal = false

  get month () {
    return moment().month(this.weatherData.DailyData.SelectedMonth - 1).format('MMMM')
  }

  get selectedMode () {
    return this.modes[this.mode]
  }

  mounted () {
    if (this.isOfferPage()) this.watchForOfferChange()
  }

  open () {
    this.modal = true
  }

  close () {
    this.modal = false
  }

  nextMode () {
    const modes = Object.keys(this.modes)
    const index = modes.indexOf(this.mode)
    this.mode = index === modes.length - 1 ? modes[0] : modes[index + 1]
  }

  previousMode () {
    const modes = Object.keys(this.modes)
    const index = modes.indexOf(this.mode)
    this.mode = index === 0 ? modes[modes.length - 1] : modes[index - 1]
  }

  pictogram (mode) {
    switch (mode) {
      case 'day': return 'sun-and-cloud'
      case 'night': return 'night'
      case 'water': return 'waves'
      default: return 'sun'
    }
  }

  temperature (mode) {
    switch (mode) {
      case 'day': return Math.round(this.weatherData.DailyData.TempDay)
      case 'night': return Math.round(this.weatherData.DailyData.TempNight)
      case 'water': return Math.round(this.weatherData.DailyData.TempWater)
      default: return Math.round(this.weatherData.DailyData.TempDay)
    }
  }
}
</script>

<style lang="scss">
.weather-widget {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #005CA9;
  text-transform: uppercase;
  max-width: 280px;
  margin: 0 auto;

  .weather-widget-body-three-params-wrapper {
    display: none;
  }

  > .weather-widget-body {
    width: 100%;
    margin-bottom: 14px;
    justify-content: space-between;
  }

  .weather-widget-temperature-wrapper {
    width: 70px;
  }

  .weather-widget__display {
      min-width: 160px;
  }

  &--three-params {
    max-width: 400px;
    margin: auto;

    > .weather-widget-body {
      display: none;
    }

    .weather-widget-body-three-params-wrapper {
      width: 100%;
      margin-bottom: 14px;
      justify-content: space-around;
      display: flex;
      flex-direction: row;

      @include media(desktop) {
        margin-bottom: 25px;
      }
    }

    .weather-widget-temperature-wrapper {
      width: 60%;
    }

    .widget__arrow {
      display: none;
    }

    .weather-widget__display {
      min-width: 110px;
    }
  }

  &-header {
    display: flex;
    justify-content: center;
    font-family: 'Gotham Book';
    font-size: .9em;
    text-transform: uppercase;
    color: #005CA9;
    margin-bottom: 11px;

    @include media(tablet) {
      font-size: .75em;
    }

    @include media(desktop) {
      font-size: .85em;
    }

    p {
      font-size: .9em !important; // need to override client custom inline styles
      padding: 0 !important; // need to override client custom inline styles
      margin: 0;

      @include media(tablet) {
        font-size: 1em !important;
      }
    }

    p:first-child {
      letter-spacing: .7px;

      @include media(tablet) {
        letter-spacing: 0;
      }
    }

    &--three-params {
      text-align: center;

      @include media(desktop) {
        min-width: 400px;
      }
    }

    &--three-params p {
      @include media(tablet) {
        font-size: 1.2em !important;
      }
    }

    &__avg-temp {
      font-weight: 600 !important; // need to override client custom inline styles
      margin-right: 2px;
      margin-bottom: 14px;

      @include media(tablet) {
        letter-spacing: -0.4px;
      }
    }

    &__month {
      margin-bottom: 14px;
    }
  }

  &-body {
    display: flex; 
    align-items: flex-end;
    flex-direction: row;
  }

  &-temperature-wrapper {
    display: flex;
    flex-direction: column;
    margin-left: 4px;
  }

  &__arrow {
    font-weight: bold;
    font-size: 10px;
    position: relative;
    bottom: 10px;
    outline: none;
    color: #005CA9;
    text-decoration: 600;

    @include media(tablet) {
      font-size: 10px;
    }
  }

  &__next {
    transform: rotate(-90deg);
    margin-right: 20px;

    @include media(tablet) {
      margin-right: 10px;
    }

    @include media(desktop) {
      margin-right: 20px;
    }
  }

  &__previous {
    transform: rotate(90deg);
    margin-left: 20px;

    @include media(tablet) {
      margin-left: 10px;
    }

    @include media(desktop) {
      margin-left: 20px;
    }
  }

  &__display {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    padding: 0 10px 0 7px;

    @include media(desktop) {
      padding: 0;
    }
  }

  &__temperature {
    font-weight: 300;
    font-size: 30px;
    line-height: 28px;
  }

  &__pictogram {
    width: 40px;
    height: 40px;
    margin: -2px 0 0 -11px;

    @include media(tablet) {
      margin: 0;
    }

    &--2-clouds {
      background: url('/assets/images/icons/weather-widget/2-clouds.svg') no-repeat;
    }

    &--cloud-3 {
      background: url('/assets/images/icons/weather-widget/cloud-3.svg') no-repeat;
    }

    &--cloud-and-rain-2 {
      background: url('/assets/images/icons/weather-widget/cloud-and-rain-2.svg') no-repeat;
    }

    &--cloud-snow {
      background: url('/assets/images/icons/weather-widget/cloud-snow.svg') no-repeat;
    }

    &--night {
      background: url('/assets/images/icons/weather-widget/night.svg') no-repeat;
    }

    &--rainy {
      background: url('/assets/images/icons/weather-widget/rainy.svg') no-repeat;
    }

    &--rainy-2 {
      background: url('/assets/images/icons/weather-widget/rainy-2.svg') no-repeat;
    }

    &--snow {
      background: url('/assets/images/icons/weather-widget/snow.svg') no-repeat;
    }

    &--storm {
      background: url('/assets/images/icons/weather-widget/storm.svg') no-repeat;
    }

    &--strong-wind {
      background: url('/assets/images/icons/weather-widget/strong-wind.svg') no-repeat;
    }

    &--sun-and-cloud {
      background: url('/assets/images/icons/weather-widget/sun-and-cloud.svg') no-repeat;
    }

    &--sun {
      background: url('/assets/images/icons/weather-widget/sun.svg') no-repeat;
    }

    &--temperature {
      background: url('/assets/images/icons/weather-widget/temperature.svg') no-repeat;
    }

    &--umbrella {
      background: url('/assets/images/icons/weather-widget/umbrella.svg') no-repeat;
    }

    &--waves {
      background: url('/assets/images/icons/weather-widget/waves.svg') no-repeat;
      height: 38px;
      position: relative;
      top: 4px;
    }

    &--windy {
      background: url('/assets/images/icons/weather-widget/windy.svg') no-repeat;
    }
  }

  &__more {
    font-size: 10px;
    font-weight: 400;
    text-transform: uppercase;
    line-height: 1;
    border-bottom: solid 1px #D2DEEB;
    margin-bottom: 3px;
    font-family: 'Gotham Book';
    cursor: pointer;

    &:hover {
      color: #009FEA;
    }

    @include media(desktop) {
      margin-bottom: 5px;
    }
  }

  &__info {
    color: #009FEA;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
    font-family: 'Gotham Book';
  }
}
</style>



// WEBPACK FOOTER //
// src/components/WeatherWidget.vue